import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, X, Star, Eye, EyeOff, ArrowUp, ArrowDown, AlertTriangle, GripVertical, Edit } from 'lucide-react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import adminPricingService, { 
  PricingFeature, 
  TierPricingFeature 
} from '../../services/adminPricingService';

interface TierPricingFeaturesManagerProps {
  tierId: string;
  tierName: string;
}

export const TierPricingFeaturesManager: React.FC<TierPricingFeaturesManagerProps> = ({
  tierId,
  tierName
}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [selectedFeatureId, setSelectedFeatureId] = useState<string>('');
  const [featureValue, setFeatureValue] = useState<string>('');
  const [displayText, setDisplayText] = useState<string>('');
  const [isHighlighted, setIsHighlighted] = useState(false);
  const [badgeText, setBadgeText] = useState<string>('');
  const [badgeColor, setBadgeColor] = useState<string>('blue');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [featureToDelete, setFeatureToDelete] = useState<{ id: string; name: string } | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingFeature, setEditingFeature] = useState<TierPricingFeature | null>(null);

  // Get all available pricing features
  const { data: allFeatures } = useQuery({
    queryKey: ['pricing-features'],
    queryFn: () => adminPricingService.getPricingFeatures(),
  });

  // Get features assigned to this tier
  const { data: tierFeaturesRaw, refetch } = useQuery({
    queryKey: ['tier-features', tierId],
    queryFn: () => adminPricingService.getTierFeatures(tierId),
  });

  // Sort features by sort_order
  const tierFeatures = tierFeaturesRaw?.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  // Mutations
  const assignFeatureMutation = useMutation({
    mutationFn: (data: any) => adminPricingService.assignFeatureToTier(tierId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tier-features', tierId] });
      setIsAssignDialogOpen(false);
      resetForm();
      toast({ title: 'Success', description: 'Feature assigned to tier' });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to assign feature',
        variant: 'destructive'
      });
    },
  });

  const updateFeatureMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => 
      adminPricingService.updateTierFeature(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tier-features', tierId] });
      toast({ title: 'Success', description: 'Feature updated' });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to update feature',
        variant: 'destructive'
      });
    },
  });

  const removeFeatureMutation = useMutation({
    mutationFn: (tierFeatureId: string) => adminPricingService.removeFeatureFromTier(tierFeatureId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tier-features', tierId] });
      setDeleteConfirmOpen(false);
      setFeatureToDelete(null);
      toast({ title: 'Success', description: 'Feature removed from tier' });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to remove feature',
        variant: 'destructive'
      });
    },
  });

  const reorderFeaturesMutation = useMutation({
    mutationFn: async (reorderedFeatures: TierPricingFeature[]) => {
      // Update sort_order for each feature
      const updatePromises = reorderedFeatures.map((feature, index) =>
        adminPricingService.updateTierFeature(feature.id, { sort_order: index + 1 })
      );
      await Promise.all(updatePromises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tier-features', tierId] });
      toast({ title: 'Success', description: 'Feature order updated' });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to reorder features',
        variant: 'destructive'
      });
    },
  });

  const moveFeatureMutation = useMutation({
    mutationFn: async ({ featureId, direction }: { featureId: string; direction: 'up' | 'down' }) => {
      if (!tierFeatures) return;
      
      const currentIndex = tierFeatures.findIndex(f => f.id === featureId);
      if (currentIndex === -1) return;
      
      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (newIndex < 0 || newIndex >= tierFeatures.length) return;
      
      // Create reordered array
      const reorderedFeatures = [...tierFeatures];
      const [movedFeature] = reorderedFeatures.splice(currentIndex, 1);
      reorderedFeatures.splice(newIndex, 0, movedFeature);
      
      // Update sort_order for each feature
      const updatePromises = reorderedFeatures.map((feature, index) =>
        adminPricingService.updateTierFeature(feature.id, { sort_order: index + 1 })
      );
      await Promise.all(updatePromises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tier-features', tierId] });
      toast({ title: 'Success', description: 'Feature moved' });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to move feature',
        variant: 'destructive'
      });
    },
  });

  const resetForm = () => {
    setSelectedFeatureId('');
    setFeatureValue('');
    setDisplayText('');
    setIsHighlighted(false);
    setBadgeText('');
    setBadgeColor('blue');
  };

  const handleAssignFeature = () => {
    if (!selectedFeatureId) return;

    // Calculate the next sort order (add to end of list)
    const nextSortOrder = (tierFeatures?.length || 0) + 1;

    assignFeatureMutation.mutate({
      tier_id: tierId,
      pricing_feature_id: selectedFeatureId,
      value: featureValue || undefined,
      display_text: displayText || undefined,
      is_highlighted: isHighlighted,
      badge_text: badgeText || null,
      badge_color: badgeColor || undefined,
      is_included: true,
      sort_order: nextSortOrder,
    });
  };

  const handleToggleIncluded = (tierFeature: TierPricingFeature) => {
    updateFeatureMutation.mutate({
      id: tierFeature.id,
      data: { is_included: !tierFeature.is_included }
    });
  };

  const handleToggleHighlighted = (tierFeature: TierPricingFeature) => {
    updateFeatureMutation.mutate({
      id: tierFeature.id,
      data: { is_highlighted: !tierFeature.is_highlighted }
    });
  };

  const handleRemoveFeature = (tierFeature: TierPricingFeature) => {
    setFeatureToDelete({
      id: tierFeature.id,
      name: tierFeature.display_text || tierFeature.pricing_feature.name
    });
    setDeleteConfirmOpen(true);
  };

  const handleEditFeature = (tierFeature: TierPricingFeature) => {
    setEditingFeature(tierFeature);
    // Pre-populate the form with existing values
    setFeatureValue(tierFeature.value || '');
    setDisplayText(tierFeature.display_text || '');
    setIsHighlighted(tierFeature.is_highlighted || false);
    setBadgeText(tierFeature.badge_text || '');
    setBadgeColor(tierFeature.badge_color || 'blue');
    setIsEditDialogOpen(true);
  };

  const handleUpdateFeature = () => {
    if (!editingFeature) return;

    updateFeatureMutation.mutate({
      id: editingFeature.id,
      data: {
        value: featureValue || undefined,
        display_text: displayText || undefined,
        is_highlighted: isHighlighted,
        badge_text: badgeText || null,
        badge_color: badgeColor || undefined,
      }
    });
    
    setIsEditDialogOpen(false);
    setEditingFeature(null);
    resetForm();
  };

  const confirmRemoveFeature = () => {
    if (featureToDelete) {
      removeFeatureMutation.mutate(featureToDelete.id);
    }
  };

  const handleDragEnd = (result: DropResult) => {
    // Check if the drag was cancelled or dropped outside
    if (!result.destination || !tierFeatures) {
      return;
    }

    // Check if the item was dropped in the same position
    if (result.destination.index === result.source.index) {
      return;
    }

    // Create a new array with the reordered items
    const items = Array.from(tierFeatures);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update the sort order and send to backend
    reorderFeaturesMutation.mutate(items);
  };

  const handleMoveUp = (featureId: string) => {
    moveFeatureMutation.mutate({ featureId, direction: 'up' });
  };

  const handleMoveDown = (featureId: string) => {
    moveFeatureMutation.mutate({ featureId, direction: 'down' });
  };

  const getAvailableFeatures = () => {
    if (!allFeatures || !tierFeatures) return [];
    const assignedFeatureIds = tierFeatures.map(tf => tf.pricing_feature_id);
    return allFeatures.filter(f => !assignedFeatureIds.includes(f.id));
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      ai: 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200',
      content: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200',
      support: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200',
      storage: 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200',
      analytics: 'bg-pink-100 text-pink-800 dark:bg-pink-900/50 dark:text-pink-200',
    };
    return colors[category] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/50 dark:text-gray-200';
  };

  return (
    <div className="flex flex-col h-full max-h-[80vh]">
      <div className="flex items-center justify-between mb-6 flex-shrink-0">
        <div>
          <h3 className="text-lg font-semibold">Pricing Plan Features</h3>
          <p className="text-sm text-muted-foreground">
            Manage features displayed on the pricing page for {tierName}. Use drag handles or arrow buttons to reorder features.
          </p>
        </div>
        <Dialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Add Feature
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Feature to {tierName}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Select Feature</Label>
                <Select value={selectedFeatureId} onValueChange={setSelectedFeatureId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a feature" />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailableFeatures().map((feature) => (
                      <SelectItem key={feature.id} value={feature.id}>
                        <div className="flex items-center space-x-2">
                          <span>{feature.name}</span>
                          <Badge className={getCategoryColor(feature.category)} variant="secondary">
                            {feature.category}
                          </Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Custom Value (optional)</Label>
                <Input
                  value={featureValue}
                  onChange={(e) => setFeatureValue(e.target.value)}
                  placeholder="e.g., 100 generations/month"
                />
              </div>
              
              <div>
                <Label>Display Text (optional)</Label>
                <Input
                  value={displayText}
                  onChange={(e) => setDisplayText(e.target.value)}
                  placeholder="Custom text to display instead of feature name"
                />
              </div>
              
              <div>
                <Label>Badge Text (optional)</Label>
                <Input
                  value={badgeText}
                  onChange={(e) => setBadgeText(e.target.value)}
                  placeholder="e.g., Coming Soon, Free Trial, Beta"
                />
              </div>
              
              <div>
                <Label>Badge Color</Label>
                <Select value={badgeColor} onValueChange={setBadgeColor}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="blue">Blue</SelectItem>
                    <SelectItem value="green">Green</SelectItem>
                    <SelectItem value="orange">Orange</SelectItem>
                    <SelectItem value="red">Red</SelectItem>
                    <SelectItem value="purple">Purple</SelectItem>
                    <SelectItem value="gray">Gray</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch
                  checked={isHighlighted}
                  onCheckedChange={setIsHighlighted}
                />
                <Label>Highlight this feature</Label>
              </div>
              
              <Button 
                onClick={handleAssignFeature}
                disabled={!selectedFeatureId || assignFeatureMutation.isPending}
                className="w-full"
              >
                {assignFeatureMutation.isPending ? 'Adding...' : 'Add Feature'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Assigned Features with Drag and Drop */}
      <div className="flex-1 overflow-hidden">
        <DragDropContext 
        onDragEnd={handleDragEnd}
        onDragStart={(start) => {
          console.log('Drag started:', start);
        }}
        onDragUpdate={(update) => {
          console.log('Drag update:', update);
        }}
      >
        <Droppable droppableId="tier-features" type="FEATURE">
          {(provided, snapshot) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className={`space-y-3 min-h-[100px] h-full overflow-y-auto pr-2 ${snapshot.isDraggingOver ? 'bg-blue-50 dark:bg-blue-950/20 rounded-lg p-2' : ''}`}
              style={{
                transition: 'background-color 0.2s ease',
              }}
            >
              {tierFeatures?.map((tierFeature, index) => (
                <Draggable 
                  key={tierFeature.id} 
                  draggableId={tierFeature.id.toString()} 
                  index={index}
                  isDragDisabled={reorderFeaturesMutation.isPending}
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={`flex items-center justify-between p-4 border rounded-lg transition-all ${
                        snapshot.isDragging 
                          ? 'shadow-lg bg-white dark:bg-slate-800 border-blue-300 dark:border-blue-600 rotate-2' 
                          : 'hover:shadow-md'
                      } ${reorderFeaturesMutation.isPending ? 'opacity-50' : ''}`}
                      style={{
                        ...provided.draggableProps.style,
                      }}
                    >
                      <div className="flex items-start space-x-3 flex-1">
                        {/* Drag Handle */}
                        <div
                          {...provided.dragHandleProps}
                          className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          title="Drag to reorder"
                          onMouseDown={(e) => {
                            console.log('Drag handle clicked', e);
                          }}
                        >
                          <GripVertical className="w-5 h-5" />
                        </div>
                        
                        <div className="flex-1">
                          <div className="mb-2">
                            <h4 className="font-medium mb-2">
                              {tierFeature.display_text || tierFeature.pricing_feature.name}
                            </h4>
                            <div className="flex items-start flex-wrap gap-2">
                              <Badge className={`${getCategoryColor(tierFeature.pricing_feature.category)} flex-shrink-0 h-auto`}>
                                {tierFeature.pricing_feature.category}
                              </Badge>
                              {tierFeature.is_highlighted && (
                                <Badge variant="secondary" className="flex-shrink-0 h-auto">
                                  <Star className="w-3 h-3 mr-1" />
                                  Highlighted
                                </Badge>
                              )}
                              {!tierFeature.is_included && (
                                <Badge variant="destructive" className="flex-shrink-0 h-auto">Not Included</Badge>
                              )}
                              {tierFeature.badge_text && (
                                <Badge 
                                  variant="outline"
                                  className={`flex-shrink-0 h-auto
                                    ${tierFeature.badge_color === 'green' ? 'border-green-500 text-green-700 bg-green-50' : ''}
                                    ${tierFeature.badge_color === 'orange' ? 'border-orange-500 text-orange-700 bg-orange-50' : ''}
                                    ${tierFeature.badge_color === 'red' ? 'border-red-500 text-red-700 bg-red-50' : ''}
                                    ${tierFeature.badge_color === 'purple' ? 'border-purple-500 text-purple-700 bg-purple-50' : ''}
                                    ${tierFeature.badge_color === 'gray' ? 'border-gray-500 text-gray-700 bg-gray-50' : ''}
                                    ${!tierFeature.badge_color || tierFeature.badge_color === 'blue' ? 'border-blue-500 text-blue-700 bg-blue-50' : ''}
                                  `}
                                >
                                  {tierFeature.badge_text}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {tierFeature.pricing_feature.description}
                          </p>
                          {tierFeature.value && (
                            <p className="text-sm font-medium text-primary mt-1">
                              Value: {tierFeature.value}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-1">
                        {/* Move Up/Down Buttons */}
                        <div className="flex flex-col">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMoveUp(tierFeature.id)}
                            disabled={index === 0 || moveFeatureMutation.isPending}
                            title="Move up"
                            className="h-6 w-6 p-0"
                          >
                            <ArrowUp className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMoveDown(tierFeature.id)}
                            disabled={index === (tierFeatures?.length || 0) - 1 || moveFeatureMutation.isPending}
                            title="Move down"
                            className="h-6 w-6 p-0"
                          >
                            <ArrowDown className="w-3 h-3" />
                          </Button>
                        </div>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleIncluded(tierFeature)}
                          title={tierFeature.is_included ? 'Mark as not included' : 'Mark as included'}
                        >
                          {tierFeature.is_included ? (
                            <Eye className="w-4 h-4 text-green-600" />
                          ) : (
                            <EyeOff className="w-4 h-4 text-red-600" />
                          )}
                        </Button>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleHighlighted(tierFeature)}
                          title={tierFeature.is_highlighted ? 'Remove highlight' : 'Highlight feature'}
                        >
                          <Star className={`w-4 h-4 ${tierFeature.is_highlighted ? 'text-yellow-500 fill-current' : 'text-gray-400'}`} />
                        </Button>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditFeature(tierFeature)}
                          title="Edit feature"
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveFeature(tierFeature)}
                          className="text-red-600 hover:text-red-700"
                          title="Remove feature"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
              
              {(!tierFeatures || tierFeatures.length === 0) && (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No pricing features assigned to this tier yet.</p>
                  <p className="text-sm">Click "Add Feature" to get started.</p>
                </div>
              )}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      </div>

      {/* Edit Feature Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Feature: {editingFeature?.display_text || editingFeature?.pricing_feature.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Feature Name</Label>
              <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded border text-sm text-muted-foreground">
                {editingFeature?.pricing_feature.name}
                <Badge className={`ml-2 ${getCategoryColor(editingFeature?.pricing_feature.category || '')}`} variant="secondary">
                  {editingFeature?.pricing_feature.category}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Base feature cannot be changed. You can customize the display text below.
              </p>
            </div>
            
            <div>
              <Label>Custom Value (optional)</Label>
              <Input
                value={featureValue}
                onChange={(e) => setFeatureValue(e.target.value)}
                placeholder="e.g., 100 generations/month"
              />
            </div>
            
            <div>
              <Label>Display Text (optional)</Label>
              <Input
                value={displayText}
                onChange={(e) => setDisplayText(e.target.value)}
                placeholder="Custom text to display instead of feature name"
              />
            </div>
            
            <div>
              <Label>Badge Text (optional)</Label>
              <Input
                value={badgeText}
                onChange={(e) => setBadgeText(e.target.value)}
                placeholder="e.g., Coming Soon, Free Trial, Beta"
              />
            </div>
            
            <div>
              <Label>Badge Color</Label>
              <Select value={badgeColor} onValueChange={setBadgeColor}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="blue">Blue</SelectItem>
                  <SelectItem value="green">Green</SelectItem>
                  <SelectItem value="orange">Orange</SelectItem>
                  <SelectItem value="red">Red</SelectItem>
                  <SelectItem value="purple">Purple</SelectItem>
                  <SelectItem value="gray">Gray</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center space-x-2">
              <Switch
                checked={isHighlighted}
                onCheckedChange={setIsHighlighted}
              />
              <Label>Highlight this feature</Label>
            </div>
            
            <div className="flex space-x-2">
              <Button 
                variant="outline"
                onClick={() => {
                  setIsEditDialogOpen(false);
                  setEditingFeature(null);
                  resetForm();
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleUpdateFeature}
                disabled={updateFeatureMutation.isPending}
                className="flex-1"
              >
                {updateFeatureMutation.isPending ? 'Updating...' : 'Update Feature'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              <span>Confirm Removal</span>
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to remove "{featureToDelete?.name}" from {tierName}? 
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDeleteConfirmOpen(false)}
              disabled={removeFeatureMutation.isPending}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmRemoveFeature}
              disabled={removeFeatureMutation.isPending}
            >
              {removeFeatureMutation.isPending ? 'Removing...' : 'Remove Feature'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};